/**
 * QuestCardForm — форма создания квеста (зелёный dashed .cform) и expanded-
 * тело карточки (эталон renderCreate js:169-193 и body js:229-269): Название/
 * Короткое → cfg-grid [Игра·Пауза | Спецверсии | Операторы | Связи | цена
 * cell-price] → сворачиваемые Расходники/Ресурсы → интеграции МК/Виджет
 * (тап-копирование) → ActionPair → генератор + сетка (SlotGridEditor).
 * Драфт формы (fdraft) живёт у родителя — collapsed-строка показывает связи
 * из драфта раскрытой карточки (эталон js:208).
 */
import { useEffect, useMemo, type Dispatch, type SetStateAction } from "react";
import type { Branch, Quest } from "../../api/types";
import type { ConsItem, QuestsAdmin } from "../../hooks/useQuestsAdmin";
import { ConsChips, ResChips } from "./ConsChips";
import { OpsRow, opsOfQuest, opsToCrew, type OpG } from "./OpsRow";
import { LinksCell, PriceRow, SpecsCell } from "./QuestCells";
import { ActionPair, CollSec, DualCell, IntegBlock } from "./QuestFormCells";
import { CodeIco, GlobeIco } from "./questIcons";
import { SlotGridEditor } from "./SlotGridEditor";
import cfg from "./config.module.css";
import css from "./quests.module.css";

/** База интеграционных URL: прод — VITE_API_URL воркера, дев — свой origin. */
const API_ORIGIN =
  ((import.meta.env?.VITE_API_URL as string | undefined) ?? "").replace(/\/$/, "") ||
  (typeof window === "undefined" ? "" : window.location.origin);

/** Партнёрские ссылки МК — ТОЛЬКО через прокладку (24.08, владелец): адрес
 *  воркера партнёру не светим, вид ссылок как у первого юнибукинга. */
const MK_PROXY_ORIGIN = "https://2026oren.ru";

/** Драфт expanded-формы (эталон fdraft js:229). cons: null = нормы не трогали. */
export interface QuestFormDraft {
  id: string; name: string; short: string; dur: number; reset: number;
  ops: OpG[]; cons: ConsItem[] | null; links: string[]; res: string[];
  pn: number; pb: number; pp: number;
  /** ID квеста в кабинете «Мир Квестов» (23.08) — старая цепочка работает против V4. */
  mk: string;
}

export function initDraft(q: Quest, qa: QuestsAdmin): QuestFormDraft {
  const p = qa.prices[q.id];
  return {
    id: q.id, name: q.name, short: q.short_name, dur: q.duration, reset: q.reset_time,
    ops: opsOfQuest(q), cons: null, links: qa.linksOf(q.id), res: qa.questRes[q.id] ?? [],
    pn: p?.threshold ?? 4, pb: p?.base_price ?? 0, pp: p?.extra_per_player ?? 0,
    mk: q.mk_id ?? "",
  };
}

export interface QuestCardBodyProps {
  q: Quest;
  branch: Branch | null;
  qa: QuestsAdmin;
  draft: QuestFormDraft;
  setDraft: Dispatch<SetStateAction<QuestFormDraft | null>>;
  onCancel: () => void;
  grid: string[] | null;
  onGrid: (next: string[] | null) => void;
  secOpen: Record<string, boolean>;
  onSec: (key: string) => void;
}

export function QuestCardBody({ q, branch, qa, draft, setDraft, onCancel, grid, onGrid, secOpen, onSec }: QuestCardBodyProps) {
  const { loadCons } = qa;
  useEffect(() => { loadCons(q.id); }, [loadCons, q.id]);

  const serverCons = useMemo<ConsItem[]>(
    () => (qa.consOf[q.id] ?? []).map((r) => ({ consumable_id: r.consumable_id, qty: r.qty, per_player: r.per_player })),
    [qa.consOf, q.id],
  );
  const cons = draft.cons ?? serverCons;
  const upd = (p: Partial<QuestFormDraft>) => setDraft((f) => (f ? { ...f, ...p } : f));
  const K = (s: string) => `${q.id}:${s}`;

  const save = () => {
    void qa.saveQuest(q, {
      name: draft.name.trim() || q.name,
      short_name: draft.short.trim() || q.short_name,
      duration: draft.dur, reset_time: draft.reset, crew_rule: opsToCrew(draft.ops),
      cons: draft.cons, res: draft.res, links: draft.links,
      pn: draft.pn, pb: draft.pb, pp: draft.pp,
      mk_id: draft.mk.trim(),
    });
  };

  return (
    <div className={css.qbody} onClick={(e) => e.stopPropagation()}>
      <div className={css.frm2}>
        <div className={css.gap2}>
          <span className={cfg.klbl}>Название</span>
          <input className={css.txt} value={draft.name} onChange={(e) => upd({ name: e.target.value })} />
        </div>
        <div className={css.gap2}>
          <span className={cfg.klbl}>Короткое имя</span>
          <input className={css.txt} value={draft.short} onChange={(e) => upd({ short: e.target.value })} />
        </div>
      </div>
      <div className={`${cfg.cfgGrid} ${css.gridTop} ${css.gridBot}`}>
        <DualCell dur={draft.dur} reset={draft.reset} onDur={(v) => upd({ dur: v })} onReset={(v) => upd({ reset: v })} />
        <SpecsCell q={q} versions={qa.versions} onBind={(v) => qa.bindVersion(v, q)} onUnbind={(v) => qa.unbindVersion(v)} />
        <OpsRow ops={draft.ops} onChange={(o) => upd({ ops: o })} />
        <LinksCell q={q} all={qa.quests} links={draft.links} onChange={(l) => upd({ links: l })} />
        <PriceRow
          pn={draft.pn} pb={draft.pb} pp={draft.pp}
          onPn={(v) => upd({ pn: v })} onPb={(v) => upd({ pb: v })} onPp={(v) => upd({ pp: v })}
        />
      </div>
      {/* A31: пустой состав снимает правило (crew_rule NULL), а operators
          (int) сервер не трогает — честно предупреждаем (иначе после reload
          карточка молча вернёт старый headcount «любых»). */}
      {draft.ops.length === 0 ? (
        <div className={cfg.helper} style={{ color: "var(--orange)" }}>
          Пустой состав снимает правило — число операторов квеста не изменится.
        </div>
      ) : null}
      <CollSec title="Расходники" count={cons.length} open={!!secOpen[K("cons")]} onToggle={() => onSec(K("cons"))}>
        <ConsChips
          items={cons}
          cat={qa.consCat}
          onChange={(list) => upd({ cons: list })}
          onAdd={(id, qty) => setDraft((f) => (f
            ? { ...f, cons: [...(f.cons ?? serverCons), { consumable_id: id, qty, per_player: 1 }] }
            : f))}
        />
      </CollSec>
      <CollSec title="Ресурсы" count={draft.res.length} open={!!secOpen[K("res")]} onToggle={() => onSec(K("res"))}>
        <ResChips ids={draft.res} cat={qa.resCat} onChange={(r) => upd({ res: r })} />
      </CollSec>
      <IntegBlock
        variant="mk" title="Мир Квестов" open={!!secOpen[K("mk")]} onToggle={() => onSec(K("mk"))}
        icon={<span style={{ color: "var(--purple)", lineHeight: 0 }}><GlobeIco /></span>}
        rows={[
          { pre: "GET", txt: `${MK_PROXY_ORIGIN}/mk/${draft.mk.trim() || q.id}/timetable` },
          { pre: "POST", txt: `${MK_PROXY_ORIGIN}/mk/${draft.mk.trim() || q.id}/book` },
          { pre: "GET", txt: `${MK_PROXY_ORIGIN}/mk/${draft.mk.trim() || q.id}/prices` },
          { pre: "GET", txt: `${MK_PROXY_ORIGIN}/mk/${draft.mk.trim() || q.id}/prepay` },
        ]}
      >
        {/* ID из кабинета МК: старая цепочка шлёт свои id — /api/mk/* поймёт их без правок у партнёра */}
        <div className={css.gap2} style={{ padding: "4px 0 2px" }}>
          <span className={cfg.klbl}>ID квеста на Мире Квестов</span>
          <input className={css.txt} placeholder="как в кабинете партнёра / старом юнибукинге" value={draft.mk}
            onChange={(e) => upd({ mk: e.target.value })} />
        </div>
      </IntegBlock>
      <IntegBlock
        variant="wg" title="Виджет" open={!!secOpen[K("wg")]} onToggle={() => onSec(K("wg"))}
        icon={<span style={{ color: "var(--cyan)", lineHeight: 0 }}><CodeIco /></span>}
        rows={[
          { pre: "GET", txt: `${API_ORIGIN}/api/public/slots?quest=${q.id}&date=ГГГГ-ММ-ДД` },
          { pre: "GET", txt: `${API_ORIGIN}/api/quest-schedule/${q.id}` },
        ]}
      />
      <ActionPair okLabel="Сохранить" onOk={save} onCancel={onCancel} />
      {/* 16.10 (владелец: «указал 75 игра и 45 перерыв, а генератор не ставит два часа между слотами»): шаг — по ТОМУ,
          ЧТО В ФОРМЕ, а не по сохранённым Игра/Пауза (было по эталону js:285,301 — пауза без «Сохранить» молча не
          учитывалась). Не сохранены — предупреждаем: бронь и сетка ДК живут по сохранённым. */}
      {draft.dur !== q.duration || draft.reset !== q.reset_time ? <div className={css.err9}>Игра/Пауза изменены, но не сохранены — нажмите «Сохранить» выше, иначе бронирование будет считать {q.duration}+{q.reset_time} мин</div> : null}
      <SlotGridEditor
        dur={draft.dur} reset={draft.reset} branch={branch}
        slots={qa.slotsOf(q.id)} draft={grid} onDraft={onGrid}
        onSave={(t) => qa.saveSlots(q.id, t)}
      />
    </div>
  );
}

export interface QuestCreateDraft { name: string; short: string; dur: number; reset: number; ops: OpG[] }

export function QuestCreateForm({ f, setF, busy, onCreate, onCancel }: {
  f: QuestCreateDraft;
  setF: Dispatch<SetStateAction<QuestCreateDraft>>;
  busy: boolean;
  onCreate: () => void;
  onCancel: () => void;
}) {
  return (
    <div className={css.cform}>
      <div className={cfg.secRow}>
        <h3 className={`${cfg.secT} ${css.cformT}`}>Новый квест</h3>
      </div>
      <div className={css.frm2}>
        <div className={css.gap2}>
          <span className={cfg.klbl}>Название *</span>
          <input
            className={css.txt} placeholder="Название квеста" value={f.name} autoFocus
            onChange={(e) => setF((p) => ({ ...p, name: e.target.value }))}
          />
        </div>
        <div className={css.gap2}>
          <span className={cfg.klbl}>Короткое имя</span>
          <input
            className={css.txt} placeholder="Для чипов и отчётов" value={f.short}
            onChange={(e) => setF((p) => ({ ...p, short: e.target.value }))}
          />
        </div>
      </div>
      <div className={cfg.cfgGrid}>
        <DualCell
          dur={f.dur} reset={f.reset}
          onDur={(v) => setF((p) => ({ ...p, dur: v }))}
          onReset={(v) => setF((p) => ({ ...p, reset: v }))}
        />
        <OpsRow ops={f.ops} onChange={(o) => setF((p) => ({ ...p, ops: o }))} />
      </div>
      <ActionPair
        okLabel={busy ? "Создаём…" : "Создать"}
        okDisabled={busy || !f.name.trim()}
        onOk={onCreate}
        onCancel={onCancel}
      />
    </div>
  );
}
